export type RichTextBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered: boolean; items: string[] }

const UNSAFE_ELEMENTS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG'])

function cleanText(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim()
}

function parseHtml(value: string): RichTextBlock[] {
  if (typeof DOMParser === 'undefined') return []
  const document = new DOMParser().parseFromString(value, 'text/html')
  const blocks: RichTextBlock[] = []

  for (const node of document.body.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = cleanText(node.textContent)
      if (text) blocks.push({ type: 'paragraph', text })
      continue
    }
    if (!(node instanceof HTMLElement) || UNSAFE_ELEMENTS.has(node.tagName)) continue

    if (node.tagName === 'UL' || node.tagName === 'OL') {
      const items = [...node.children]
        .filter((child) => child.tagName === 'LI')
        .map((child) => cleanText(child.textContent))
        .filter(Boolean)
      if (items.length) blocks.push({ type: 'list', ordered: node.tagName === 'OL', items })
      continue
    }

    if (node.tagName === 'LI') {
      const text = cleanText(node.textContent)
      if (text) blocks.push({ type: 'list', ordered: false, items: [text] })
      continue
    }

    const text = cleanText(node.textContent)
    if (text) blocks.push({ type: 'paragraph', text })
  }

  return blocks
}

function parsePlainText(value: string): RichTextBlock[] {
  const blocks: RichTextBlock[] = []
  let paragraph: string[] = []
  let listItems: string[] = []

  const flushParagraph = () => {
    const text = cleanText(paragraph.join(' '))
    if (text) blocks.push({ type: 'paragraph', text })
    paragraph = []
  }
  const flushList = () => {
    if (listItems.length) blocks.push({ type: 'list', ordered: false, items: listItems })
    listItems = []
  }

  for (const rawLine of value.replace(/\r\n?/g, '\n').split('\n')) {
    const line = rawLine.trim()
    if (!line) {
      flushParagraph()
      flushList()
      continue
    }

    const bullet = line.match(/^(?:[-*•]|\d+[.)])\s+(.+)$/)
    if (bullet?.[1]) {
      flushParagraph()
      listItems.push(cleanText(bullet[1]))
      continue
    }

    flushList()
    paragraph.push(line)
  }

  flushParagraph()
  flushList()
  return blocks
}

export function formatPublicDescription(value: string | null | undefined): RichTextBlock[] {
  const source = value?.trim()
  if (!source) return []
  const htmlBlocks = /<\/?[a-z][\s\S]*>/i.test(source) ? parseHtml(source) : []
  return htmlBlocks.length ? htmlBlocks : parsePlainText(source)
}
